// © Thibault Sabathier, 2025. Tous droits réservés.
// contexts/RecruiterAuthContext.js
//
// Auth context DÉDIÉ au module Recruteur (commit 7a).
// Distinct de `AuthContext` member car :
//   - Cookie httpOnly distinct (`recruiter_auth_token`)
//   - Endpoints distincts (`/api/recruiters/auth/*` + `/api/recruiters/me`)
//   - localStorage distinct (`recruiterId`, `recruiterUuid`) pour éviter
//     toute collision avec `userId` member.
//   - Source de vérité : `GET /api/recruiters/me` (le state local n'est
//     qu'un cache UI, le serveur valide le cookie httpOnly).
//
// Au mount du Provider, on appelle `/me` une fois pour hydrater le state
// si une session existe déjà. Si 401 → utilisateur pas connecté (pas
// d'erreur, juste state vide). Si 404 RECRUITER_MODULE_DISABLED → flag
// `moduleDisabled=true` pour que le router puisse afficher la page dédiée.
//
// Le `RecruiterLayout` consomme uniquement ce context (consigne user).

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react'
import api from '../utils/recruiterApiClient'

const RecruiterAuthContext = createContext(null)

// Clés localStorage dédiées (zéro collision avec userId/userRole member)
const LS_RECRUITER_ID = 'recruiterId'
const LS_RECRUITER_UUID = 'recruiterUuid'

const clearRecruiterStorage = () => {
  localStorage.removeItem(LS_RECRUITER_ID)
  localStorage.removeItem(LS_RECRUITER_UUID)
}

export const RecruiterAuthProvider = ({ children }) => {
  // State : recruteur connecté + branding tenant
  const [recruiter, setRecruiter] = useState(null)
  const [organization, setOrganization] = useState(null)
  // moduleDisabled = backend a renvoyé 404 RECRUITER_MODULE_DISABLED
  // → router peut afficher <RecruiterModuleDisabled />
  const [moduleDisabled, setModuleDisabled] = useState(false)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  /**
   * Charge l'état recruteur depuis `/api/recruiters/me`.
   * Silencieux sur 401 (pas connecté) et 404 (module disabled).
   * Le caller peut être au mount (initial) ou après login (refresh).
   */
  const refresh = useCallback(async () => {
    try {
      setLoading(true)
      const data = await api.get('/recruiters/me')
      setRecruiter(data.recruiter || null)
      setOrganization(data.organization || null)
      setModuleDisabled(false)
      setError(null)
      if (data.recruiter) {
        localStorage.setItem(LS_RECRUITER_ID, String(data.recruiter.id))
        if (data.recruiter.uuid) {
          localStorage.setItem(LS_RECRUITER_UUID, data.recruiter.uuid)
        }
      } else {
        clearRecruiterStorage()
      }
      return data
    } catch (err) {
      // 401 = pas connecté (cookie absent / invalide)
      if (err.status === 401) {
        setRecruiter(null)
        setOrganization(null)
        setModuleDisabled(false)
        clearRecruiterStorage()
        return null
      }
      // 404 RECRUITER_MODULE_DISABLED = module pas activé pour ce tenant
      if (err.code === 'RECRUITER_MODULE_DISABLED') {
        setRecruiter(null)
        setOrganization(null)
        setModuleDisabled(true)
        clearRecruiterStorage()
        return null
      }
      // Autre erreur : on la remonte pour debug
      setError(err)
      throw err
    } finally {
      setLoading(false)
    }
  }, [])

  // Au mount : tenter de récupérer une session existante
  useEffect(() => {
    refresh().catch(() => {})
  }, [refresh])

  /**
   * Login : POST /auth/login → cookie posé par backend → refresh /me.
   * Renvoie `{ recruiter, isPending }` ou throw AppError.
   */
  const login = useCallback(
    async ({ email, password }) => {
      const resp = await api.post('/recruiters/auth/login', { email, password })
      // Le cookie est posé par le backend, on hydrate via /me
      await refresh()
      return resp
    },
    [refresh],
  )

  /**
   * Signup : POST /auth/signup. NE log PAS automatiquement (le backend
   * crée en status='pending', email_verified=false).
   * Le caller redirige vers une page "vérifiez votre email".
   */
  const signup = useCallback(async (payload) => {
    return await api.post('/recruiters/auth/signup', payload)
  }, [])

  /**
   * Logout : POST /auth/logout (backend révoque session DB + clear cookie)
   * + clear state local.
   */
  const logout = useCallback(async () => {
    try {
      await api.post('/recruiters/auth/logout')
    } catch (err) {
      // Best-effort : si le backend refuse, on clear quand même côté front
      console.warn('[RecruiterAuth] logout API error', err.message)
    }
    setRecruiter(null)
    setOrganization(null)
    clearRecruiterStorage()
  }, [])

  /**
   * Vérifie l'email recruteur via le token reçu par mail.
   * Stateless : ne change pas le state recruteur (le user n'est pas connecté
   * au moment de la vérif). La page caller redirige vers /login après succès.
   */
  const verifyEmail = useCallback(async (token) => {
    return await api.post('/recruiters/auth/verify-email', { token })
  }, [])

  /**
   * Demande un email de réinitialisation. Le backend renvoie TOUJOURS 200
   * (anti enum-attack), même si l'email n'existe pas. La page caller affiche
   * un message générique de confirmation.
   */
  const forgotPassword = useCallback(async (email) => {
    return await api.post('/recruiters/auth/forgot-password', { email })
  }, [])

  /**
   * Réinitialise le mot de passe via le token reçu par mail.
   * Le backend révoque toutes les sessions existantes et clear le cookie.
   * La page caller redirige vers /login pour relogin propre.
   */
  const resetPassword = useCallback(async (token, newPassword) => {
    return await api.post('/recruiters/auth/reset-password', { token, newPassword })
  }, [])

  /**
   * Met à jour le profil recruteur (firstName, lastName, description, logoPath).
   * Le backend `PATCH /recruiters/me` rejette les champs non whitelisted
   * (email, siret, status — protégés). Refresh du state local après succès.
   */
  const updateProfile = useCallback(
    async (patch) => {
      const data = await api.patch('/recruiters/me', patch)
      if (data?.recruiter) {
        setRecruiter(data.recruiter)
      }
      return data
    },
    [],
  )

  /**
   * Upload du logo entreprise (multipart/form-data).
   * `recruiterApiClient` détecte FormData et drop le Content-Type pour
   * laisser le browser ajouter la boundary multipart.
   * Backend resize en 400x400 cover, cleanup l'ancien fichier, met à jour
   * `recruiter.logoPath` et renvoie le payload recruteur frais.
   */
  const uploadLogo = useCallback(async (file) => {
    const formData = new FormData()
    formData.append('images', file) // convention uploadService = champ "images"
    const data = await api.post('/recruiters/me/logo', formData)
    if (data?.recruiter) {
      setRecruiter(data.recruiter)
    }
    return data
  }, [])

  // Flags dérivés exposés pour faciliter les conditions UI
  const isAuthenticated = !!recruiter
  const isPending = !!recruiter && recruiter.status === 'pending'
  const isValidated = !!recruiter && recruiter.status === 'validated'

  return (
    <RecruiterAuthContext.Provider
      value={{
        recruiter,
        organization,
        loading,
        error,
        moduleDisabled,
        isAuthenticated,
        isPending,
        isValidated,
        login,
        signup,
        logout,
        refresh,
        verifyEmail,
        forgotPassword,
        resetPassword,
        updateProfile,
        uploadLogo,
      }}
    >
      {children}
    </RecruiterAuthContext.Provider>
  )
}

export const useRecruiterAuth = () => {
  const ctx = useContext(RecruiterAuthContext)
  if (!ctx) {
    throw new Error('useRecruiterAuth must be used within a RecruiterAuthProvider')
  }
  return ctx
}

export default RecruiterAuthContext
